// @flow
import React, {useEffect, useState} from 'react';
import {Switch, Route, useHistory} from 'react-router-dom';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import queryString from 'query-string';
import {makeStyles} from '@material-ui/core/styles';
import {FileCopyOutlined} from '@material-ui/icons';
import {
  ClickAwayListener,
  Hidden,
  IconButton,
  Tooltip,
} from '@material-ui/core';

import {colors} from '../../../assets/styles/colors';

import shareToFbExample from '../../../assets/img/shareToFbExample.png';
import shareToInstagramExample from '../../../assets/img/shareToInstagramExample.png';

import {LEGACY_TIERS} from '../../../entities';
import type {
  User,
  Store,
  ResellerInformation,
  SocialMedia,
  Subscription,
  SubscriptionSetting,
} from '../../../entities';

import {usePage} from '../../page/utils';
import {usePrevious} from '../../../utils/react';

import ComparisonTierModal from '../../../elements/ComparisonTierModal/ComparisonTierModal';
import TestOrdersModal from '../../../elements/TestOrdersModal/TestOrdersModal';

import ShareSocialsCard from './ShareSocialsCard';
import ReferralModal from './Modals/ReferralModal';
import {UploadProductModal} from './Modals/UploadProuctModal';
import analytics from '../../../analytics';

import {getCustomDomain, useIsFeatureInTier} from '../../../utils/component';
import OnboardingConfig from '../../onboarding-config';
import {useOnboardingProgress} from '../../onboarding-config/utils';
import FreeTrialBanner from '../../../elements/FreeTrialBanner/FreeTrialBanner';

import type {DashboardAction, DomainExpositionData} from '../../dashboard/duck';

type Props = {|
  user?: User,
  store?: Store,
  resellerInformation: ResellerInformation,
  isFetchingResellerInformation: boolean,
  socialMedia?: SocialMedia[],
  fetchPageViews: () => void,
  pageViews?: number,
  fetchProducts: ({queryFilter?: {[key: string]: string}}) => void,
  productsCount?: number,
  fetchOrdersCount: ({noFilters: boolean}) => void,
  ordersCount?: number,
  categoriesCount?: number,

  completeFirstSteps: () => void,
  fetchCompleteFirstSteps: () => void,
  firstStepsCompleted?: boolean,
  isCheckingIfFirstStepsCompleted?: boolean,
  domainExposition: (payload: DashboardAction) => void,
  fetchDomainExposition: () => void,
  domainExpositionData?: DomainExpositionData,
  referreds?: {[key: string]: any}[],
  points?: number,
  subscription?: Subscription,
  subscriptionSetting?: SubscriptionSetting,
  fetchSummaryProductsCount: () => void,
  saveCompletedConfig: () => void,
  completedConfig?: boolean,
  dueInformation?: Subscription,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflowX: 'hidden',
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 64,
    },
  },
  greeting: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(4, 0, 0, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(4, 2, 0, 2),
    },
  },
  bold: {
    fontWeight: 600,
  },
  welcome: {
    margin: theme.spacing(2, 0, 0, 4),
  },
  userName: {
    fontSize: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  textGreetings: {
    fontSize: '1.3rem',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1300,
    // [theme.breakpoints.down('md')]: {
    //   alignItems: 'center',
    //   marginBottom: 180,
    // },
    margin: theme.spacing(2, 4, 4, 4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
  },
  topContent: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  leftContent: {
    minWidth: 320,
    // flexShrink: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '16px',
    backgroundColor: '#7B39FF',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      // maxWidth: 360,
      width: '100%',
      marginTop: theme.spacing(4),
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 'unset',
      padding: theme.spacing(3, 2, 2, 2),
    },
    background: 'white',
  },
  bottomFixed: {
    [theme.breakpoints.up('md')]: {
      marginBottom: '1rem',
    },
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(4),
    },
  },
  rightContentTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  rightContentTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
    },
  },
  tutorialsButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      padding: 0,
      boxShadow: 'unset',
    },
  },
  progressBar: {
    // marginTop: theme.spacing(1),
  },
  actionButtons: {
    marginTop: theme.spacing(1),
    height: '100%',
  },
  actionButton: {
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    // flexDirection: 'column',
    alignItems: 'center',
    '& .MuiButton-label': {
      height: '100%',
      display: 'flex',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'column',
      },
      // justifyContent: 'space-between',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
    padding: theme.spacing(2, 4),
  },
  urlFreeStore: {
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4),
  },
  actionButtonLabel: {
    margin: theme.spacing(2),
    whiteSpace: 'pre-line',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: '0',
    },
  },
  actionButtonImage: {
    margin: theme.spacing(0, 2),
  },
  thirdActionButton: {
    [theme.breakpoints.only('xs')]: {
      '& .MuiButton-label': {
        flexDirection: 'row',
      },
    },
    [theme.breakpoints.only('md')]: {
      '& .MuiButton-label': {
        flexDirection: 'row',
      },
    },
  },
  availableSoon: {
    color: colors.grey800,
    fontSize: '0.75rem',
    position: 'absolute',
    top: 12,
    left: 12,
    width: 65,
    whiteSpace: 'break-spaces',
    lineHeight: '0.875rem',
    textAlign: 'left',
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(1),
      position: 'unset',
      width: 'unset',
      textAlign: 'unset',
    },
  },
  actionButtonLabel2: {
    fontWeight: 500,
  },
  actionButtonBigNumber: {
    marginTop: theme.spacing(1),
    fontSize: '2.5rem',
    fontWeight: 600,
    width: '5rem',
  },
  actionButtonInfoIcon: {
    marginLeft: 'auto',
  },
  infoTooltip: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  actionButtonBottomButton: {
    position: 'relative',
    bottom: theme.spacing(1),
    fontSize: '0.875rem',
    '& .MuiButton-label': {
      flexDirection: 'row',
    },
    whiteSpace: 'normal',
    margin: theme.spacing(0, -3),
    lineHeight: '1.125rem',
    marginBottom: theme.spacing(-2),
  },
  demoDayActionButton: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.lightPurple,
    color: colors.purple3,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    '& .MuiButton-label': {
      height: '100%',
      display: 'flex',
      // justifyContent: 'space-between',
    },
    padding: theme.spacing(2, 5),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1),
      '& .MuiButton-label': {
        flexDirection: 'column',
      },
    },
    [theme.breakpoints.only('md')]: {
      padding: theme.spacing(2, 1),
      '& .MuiButton-label': {
        flexDirection: 'column',
      },
    },
  },
  demoDayActionButtonImage: {
    [theme.breakpoints.only('sm')]: {
      marginRight: theme.spacing(9),
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(9),
    },
  },
  demoDayActionButtonLabel: {
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 600,
    whiteSpace: 'normal',
    lineHeight: '1.75rem',
    [theme.breakpoints.only('xs')]: {
      lineHeight: '1.5rem',
      fontSize: '1rem',
    },
    [theme.breakpoints.only('md')]: {
      lineHeight: '1.5rem',
      fontSize: '1rem',
    },
  },
  demoDayActionButtonSubLabel: {
    textAlign: 'center',
    marginTop: 8,
    fontSize: '1rem',
    fontWeight: 600,
    [theme.breakpoints.only('xs')]: {
      fontSize: '0.75rem',
    },
    [theme.breakpoints.only('md')]: {
      fontSize: '0.75rem',
    },
  },

  currentTier: {
    backgroundColor: colors.lightPurple,
    color: colors.purple3,
    borderRadius: '20px',
    padding: '4px 11px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  tierName: {
    marginTop: theme.spacing(1),
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    marginTop: theme.spacing(2),
  },
  dividerBenefits: {
    background: 'rgba(255, 255, 255, 0.15)',
    width: '100%',
    marginTop: theme.spacing(2),
  },
  featureList: {
    marginTop: theme.spacing(3),
    marginBottom: 'auto',
  },
  featureItem: {
    marginBottom: theme.spacing(1),
    color: colors.grey700,
  },
  videoButton: {
    margin: theme.spacing(1, 0),
  },
  promoteUrStoreButton: {
    width: '100%',
  },

  bottomContent: {
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(4),
    },
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    background: colors.purple3,
  },
  rightTitle: {
    color: colors.grey100,
    fontSize: '1.375rem',
    fontWeight: 600,
    marginRight: theme.spacing(3),
  },
  rightHeader: {
    margin: theme.spacing(3, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  upgradeButtom: {
    height: 44,
    backgroundColor: '#FFDF65',
    color: colors.purple3,
    fontWeight: 600,
    fontSize: '1.125rem',
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, 4, 4, 4),
      width: 'calc(100% - 64px)',
    },
  },
  premiumFeaturesContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    '& img': {
      maxWidth: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(4),
    },
  },
  arrowWrapper: {
    // height: 504,
    width: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  premiumFeaturesPages: {
    display: 'flex',
    overflowX: 'scroll',
    scrollbarWidth: 0,
    '&::-webkit-scrollbar': {display: 'none'},
    '-ms-overflow-style': 'none',
  },
  premiumFeature: {
    marginLeft: theme.spacing(2),
  },
  arrowButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.25) !important',
    color: 'white',
    padding: theme.spacing(1),
  },
  extraButtons: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 4, 4, 4),
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  extraButton: {
    backgroundColor: '#6926F1',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(4),
    justifyContent: 'space-evenly',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      padding: theme.spacing(2),
      justifyContent: 'space-evenly',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row-reverse',
    },
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    // alignItems: 'center',
  },
  extraButtonText: {
    textAlign: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: 'auto',
    [theme.breakpoints.up('md')]: {
      fontSize: '22px',
    },
  },
  extraButtonButton: {
    marginTop: theme.spacing(2),
    padding: '6px 24px',
    color: 'white',
    border: '1px solid white',
  },
  contactAlert: {
    marginTop: theme.spacing(2),
  },
  yMas: {
    marginTop: theme.spacing(5),
    fontSize: '1.375rem',
    fontWeight: 600,
    color: 'white',
  },
  shareSocialsDescription: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  shareSocialsCards: {
    marginTop: theme.spacing(4),
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  shareSocialsCardContent: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(2),
    color: colors.grey700,
    fontSize: '0.875rem',
    '& p': {
      margin: '12px 0 0 0',
    },
    '& b': {
      fontWeight: 600,
    },
  },
  shareSocialsCardImage: {
    marginTop: theme.spacing(2),
    maxWidth: '100%',
  },

  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  logo: {
    height: 48,
    width: 'auto',
  },
  extraButtonReferreds: {
    backgroundColor: '#6926F1',
    borderRadius: '16px',
    padding: theme.spacing(4),
    justifyContent: 'space-evenly',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.between('md', 'lg')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      justifyContent: 'space-evenly',
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(4, 2, 2, 2),
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
    // alignItems: 'center',
  },
  containerEarnedPoints: {
    height: '100%',
    backgroundColor: '#F5F0FF',
    borderRadius: '8px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  wrapperImgReferred: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-evenly',
      alignItems: 'center',
    },
  },
  inviteFriend: {
    // width: '6rem',
    width: '100px',
    margin: '0',
  },
  inviteText: {
    textAlign: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '18px',
    margin: 0,
  },
  earnedText: {
    textAlign: 'center',
    color: '#5E1FDB',
    fontSize: '14px',
    fontWeight: 500,
    [theme.breakpoints.up('md')]: {
      fontSize: '16px',
    },
  },
  smallEarnedText: {
    color: '#5E1FDB',
    fontSize: '12px',
    fontWeight: 500,
  },
  earnedPoints: {
    color: '#5E1FDB',
    fontSize: '24px',
    fontWeight: 600,
  },
  btnKnowReferral: {
    backgroundColor: '#FFDF65',
    borderRadius: '32px',
    color: '#500BDB',
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: '#FBBF34!important',
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '2rem',
      paddingRight: '2rem',
    },
  },
  containerShowPoints: {
    width: '100%',
    background: '#F5F0FF',
    borderRadius: '8px',
    padding: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      width: '12.5rem',
    },
  },
  showPoints: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  referralBtn: {
    padding: '0',
    display: 'inline',
  },
  smallTextGray: {
    color: '#9595AB',
    fontSize: '12px',
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
  },
  completeSteps: {
    color: '#FFFFFF',
    fontSize: '14.4px',
    fontWeight: 600,
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: '20px',
    },
  },
  bgSpan: {
    backgroundColor: '#FFFFFF',
    color: '#7B39FF',
    padding: '4px',
  },
  underlineSpan: {
    borderBottom: '3px solid #FEC643',
  },
  containerGirlSteps: {
    width: '50%',
    '& img': {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  card: {
    position: 'relative',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F9F6FD 100%)',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3, 2),
    marginTop: '-8px',
  },
  cardText: {
    color: '#3A3A49',
    fontWeight: 500,
    fontSize: '14px',
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
    // whiteSpace: 'nowrap',
    // overflow: 'hidden',
  },
  cardFooter: {
    marginTop: theme.spacing(1),
    color: '#3A3A49',
    fontWeight: 400,
    fontSize: '12px',
    textAlign: 'center',
  },
  floatWssp: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    '& img ': {
      width: '2.3rem',
    },
  },
  containerKnowMore: {
    margin: '1rem 0',
    padding: '8px',
    borderRadius: '12px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  knowMore: {
    backgroundColor: '#fdf5d6',
    borderRadius: '9px',
    padding: theme.spacing(2),
    position: 'relative',
    '& img': {
      position: 'absolute',
      bottom: 0,
      right: 0,
    },
  },
  knowTitle: {
    color: '#1E1E26',
    fontWeight: 600,
    fontSize: '16px',
    [theme.breakpoints.up('md')]: {
      fontSize: '18px',
    },
  },
  knowSubtitle: {
    fontWeight: 500,
    fontSize: '14px',
  },
  knowFooter: {
    marginTop: '0.75rem',
    fontWeight: 500,
    fontSize: '12px',
    width: '70%',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
  btnKnow: {
    fontSize: '12px',
  },
  shopTest: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.between('sm', 'md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    [theme.breakpoints.between('md', 'lg')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('lg')]: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  wrapperImgShopTest: {
    // margin: '1rem auto',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '-30px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '50%',
    },
    [theme.breakpoints.up('md')]: {
      width: '22.5rem',
      margin: '0 auto',
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '-2rem',
    },
    [theme.breakpoints.up('xl')]: {
      width: '30rem',
    },
  },
  imgShopTest: {
    width: '100%',
    position: 'relative',
    backgroundColor: '#F8F5FE',
    padding: theme.spacing(2),
    borderRadius: '9px',
    height: '9rem',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    '& img': {
      position: 'absolute',
      bottom: 0,
      right: 0,
    },
    [theme.breakpoints.up('md')]: {
      width: '100%',
      height: '12.5rem',
    },
  },
  textImgShopTest: {
    fontWeight: 600,
    fontSize: '12px',
    [theme.breakpoints.up('md')]: {
      fontSize: '14px',
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: '16px',
    },
  },
  containerStepInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  textInfo: {
    fontSize: '14px',
    color: '#55556D',
  },
  numberStepInfo: {
    width: '1.7rem',
    height: '1.7rem',
    fontSize: '12px',
    padding: theme.spacing(1),
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    color: '#9595AB',
    border: '1px solid #E8E8ED',
    marginRight: theme.spacing(1),
  },
  wrapperSteps: {
    [theme.breakpoints.down('md')]: {
      backgroundColor: '#FFFFFF',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
      padding: theme.spacing(2),
    },
  },
  startToSell: {
    marginTop: 'unset',
    [theme.breakpoints.down('md')]: {
      fontWeight: 500,
      color: colors.grey900,
      textAlign: 'center',
    },
  },
  wrapperProgress: {
    marginTop: theme.spacing(1),
    width: '100%',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      justifyContent: 'space-between',
      '& > .MuiBox-root': {
        width: '15rem',
      },
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  seeStore: {
    textDecoration: 'underline',
    fontSize: '14px',
    color: '#55556D',
  },
  storeLinkTooltip: {
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
    [theme.breakpoints.up('md')]: {
      maxWidth: 300,
    },
  },
  infoBtn: {
    color: '#9595AB',
    marginLeft: theme.spacing(1),
  },
  textTooltip: {
    color: '#55556D',
    fontSize: '11px',
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
  },
  wrapperGirlSteps: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  useFreeTrial: {
    background: '#FFFFFF',
    margin: '1rem 0',
    padding: theme.spacing(2, 1),
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  textFreeTrial: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#3A3A49',
    textAlign: 'center',
  },
  textFreeTrialSmall: {
    fontSize: '12px',
    fontWeight: 500,
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: '#3A3A49',
  },
  openFreeTrial: {
    margin: theme.spacing(2, 0),
  },
  wrapperUrlStore: {
    display: 'flex',
    flexDirection: 'column',
    color: '#717190',
    [theme.breakpoints.up('lg')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  textLinkStore: {
    fontSize: '12px',
    [theme.breakpoints.up('md')]: {
      fontSize: '14px',
    },
  },
  linkStore: {
    fontWeight: 500,
    fontSize: '14px',
    [theme.breakpoints.up('lg')]: {
      fontSize: '16px',
      marginLeft: theme.spacing(2),
    },
  },
  wrapActionButton: {
    display: 'flex',
    alignItems: 'center',
    width: '12rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  contentBelieve: {
    width: '100%',
    padding: theme.spacing(2),
    background: 'linear-gradient(180deg, #F7F8FA 0%, #EDF1F7 100%)',
    borderRadius: '10px',
  },
  believeText: {
    textAlign: 'center',
    color: '#3A3A49',
    fontSize: '20px',
    fontWeight: 600,
    '& span': {
      backgroundColor: '#7B39FF',
      padding: theme.spacing(0, 1),
      color: '#FFFFFF',
    },
  },
  cardIg: {
    marginTop: '-6px',
    padding: theme.spacing(2),
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  textIg: {
    textAlign: 'center',
    color: '#3A3A49',
    fontSize: '14px',
    fontWeight: 500,
  },
  wrapperBelieve: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  orderDone: {
    fontSize: '12px',
    margin: '0',
    color: '#7330F9',
    '& .MuiButtonBase-root ': {
      fontSize: '12px',
    },
    marginLeft: '30px',
    marginTop: '-1.3rem',
    marginBottom: '1rem',
  },
  notUsedYet: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  igButton: {
    marginLeft: '8px',
    padding: theme.spacing(1),
    background: '#7B39FF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    color: '#FFFFFF',
  },
  wrapperIgBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& img ': {
      width: '1rem',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row-reverse',
      '& img ': {
        width: '1.5rem',
        marginTop: '-16px',
      },
    },
  },
  textIgSmall: {
    fontWeight: 500,
    fontSize: '10px',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      fontSize: '12px',
    },
  },
  benefits: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  benefitText: {
    color: '#FFFFFF',
    fontWeight: 500,
    fontSize: '14px',
  },
  explanationVideo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    background: 'rgba(237, 228, 255, 0.5)',
    borderRadius: '8px',
  },
  explanationText: {
    fontWeight: 500,
    fontSize: '14px',
  },
  playIcon: {
    color: '#bd9cff',
  },
  titleStep: {
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.grey1000,
  },
  wrapTitle: {
    display: 'flex',
    width: '100%',
  },
  containerStepButton: {
    marginTop: theme.spacing(1),
  },
  stepButton: {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  imgContentStep: {
    margin: theme.spacing(1.5, 0),
  },
  mainContentStep: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      minHeight: '180px',
    },
  },
  proBenefits: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  benefitsContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  benefitPurpleText: {
    fontSize: 14,
    fontWeight: 500,
  },
  benefitSubtitle: {
    color: colors.grey800,
    fontWeight: 500,
    fontSize: 14,
  },
  benefitTitle: {
    color: colors.grey900,
    fontSize: 16,
    fontWeight: 600,
  },
  helpCenter: {
    marginTop: theme.spacing(2),
    width: '100%',
    background: '#25D366',
    borderRadius: '12px',
    color: '#FFFFFF',
    '&:hover': {
      background: '#25D366',
    },
  },
  contactWssp: {
    fontSize: 14,
    fontWeight: 600,
  },
  cardBottomContent: {
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textCompleted: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
  },
  noProducts: {
    color: 'rgba(0, 0, 0, 0.26)',
  },
}));

export default usePage<Props>(Home, 'Home');

function Home(props: Props) {
  const classes = useStyles();
  const {
    user,
    store,
    fetchProducts,
    fetchOrdersCount,
    ordersCount,
    fetchPageViews,
    firstStepsCompleted,
    domainExposition,
    fetchDomainExposition,
    domainExpositionData,
    fetchSummaryProductsCount,
    saveCompletedConfig,
    completedConfig,
  } = props;

  const history = useHistory();
  const queryObject = queryString.parse(history.location.search);
  const prevStepsCompleted = usePrevious(firstStepsCompleted);

  const [orderModal, setOrderModal] = useState(false);
  const [uploadProductModal, setUploadProductModal] = useState(false);

  useEffect(() => {
    if (queryObject.first_sale && queryObject.first_sale === '1') {
      if (
        typeof ordersCount !== 'undefined' &&
        typeof firstStepsCompleted === 'boolean' &&
        !ordersCount &&
        !firstStepsCompleted
      )
        setOrderModal(true);
    }
  }, [firstStepsCompleted, ordersCount, queryObject]);

  useEffect(() => {
    fetchProducts({queryFilter: {size: '0'}});
  }, [fetchProducts]);

  useEffect(() => {
    if (user) {
      fetchSummaryProductsCount();
    }
  }, [user, fetchSummaryProductsCount]);

  const hasBasicDashboardFeature = useIsFeatureInTier('DASHBOARD_BASIC');
  const hasProDashboardFeature = useIsFeatureInTier('DASHBOARD_PRO');

  const hasDashboardFeature =
    hasBasicDashboardFeature || hasProDashboardFeature;
  useEffect(() => {
    if (
      store &&
      // LEGACY_TIERS.includes(store.storeTier.name) &&
      !hasDashboardFeature
    ) {
      const orderStates = [
        'PENDING',
        'CONFIRMED',
        'PAID',
        'ON_THE_WAY',
        'DELIVERED',
      ];
      fetchOrdersCount({noFilters: true, orderStates});
      fetchPageViews();
    }
  }, [fetchOrdersCount, fetchPageViews, hasDashboardFeature, store]);

  useEffect(() => {
    if (store && LEGACY_TIERS.includes(store.storeTier.name)) {
      fetchDomainExposition();
    }
  }, [fetchDomainExposition, store]);

  const progress = useOnboardingProgress();

  useEffect(() => {
    if (progress && !completedConfig && progress >= 100) {
      saveCompletedConfig();
      analytics.fireCustomEvent('Store Configurations Completed', undefined, [
        'user-guiding',
      ]);
    }
  }, [saveCompletedConfig, progress, store, completedConfig]);

  useEffect(() => {
    if (
      typeof prevStepsCompleted !== 'undefined' &&
      !prevStepsCompleted &&
      firstStepsCompleted
    ) {
      history.replace('/home');
    }
  }, [firstStepsCompleted, history, prevStepsCompleted]);

  const [linkCopied, setLinkCopied] = useState(0);

  const [comparisonModal, setComparisonModal] = useState(false);
  const [referralModal, setReferralModal] = useState(false);

  return (
    <div className={classes.root}>
      <Switch>
        <Route exact path="/home">
          <OnboardingConfig />
        </Route>
        <Route exact path="/home/share-socials">
          <main className={classes.content}>
            <div className={classes.greeting}>
              <Typography className={classes.userName}>
                Hola{' '}
                <span className={classes.bold}>{user && user.firstName}!</span>
              </Typography>
              <Typography className={classes.textGreetings}>
                <Hidden mdUp>Tu tienda virtual ha sido creada</Hidden>
                <Hidden smDown>Bienvenida a tu tienda virtual</Hidden>
              </Typography>
            </div>

            <FreeTrialBanner />
            <Typography className={classes.shareSocialsDescription}>
              Comparte el URL de tu tienda virtual en tus redes sociales.
            </Typography>
            <div className={classes.shareSocialsCards}>
              <ShareSocialsCard
                title="Facebook"
                completed={
                  domainExpositionData && domainExpositionData.facebook
                }
                onDoneButtonClick={() =>
                  domainExposition({
                    domainExpositionRequest: {facebook: true},
                  })
                }
              >
                <div className={classes.shareSocialsCardContent}>
                  <p>1. Ingresa a tu página en Facebook.</p>
                  <p>
                    2. Clic en <b>configuración.</b>
                  </p>
                  <p>
                    3. Clic en <b>Editar Página.</b>
                  </p>
                  <p>
                    4. Copia tu dominio:{' '}
                    <b className={classes.shareSocialsDomain}>
                      {!!store && getCustomDomain(store)}
                    </b>
                    <ClickAwayListener onClickAway={() => setLinkCopied(0)}>
                      <Tooltip
                        placement="right"
                        arrow
                        // PopperProps={{
                        //   disablePortal: true,
                        // }}
                        onClose={() => setLinkCopied(0)}
                        open={linkCopied === 1}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        leaveDelay={300}
                        title="¡Link copiado!"
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            !!store &&
                              store.domains[0].domainName &&
                              navigator &&
                              navigator.clipboard &&
                              navigator.clipboard
                                .writeText(`https://${getCustomDomain(store)}`)
                                .then(() => setLinkCopied(1))
                                .catch(e => console.error(e));
                          }}
                        >
                          <FileCopyOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ClickAwayListener>
                  </p>
                  <p>5. Pegarlo como sitio web</p>
                  <img
                    className={classes.shareSocialsCardImage}
                    src={shareToFbExample}
                    alt="compartir en facebook"
                  />
                </div>
              </ShareSocialsCard>
              <ShareSocialsCard
                title="Instagram"
                completed={
                  domainExpositionData && domainExpositionData.instagram
                }
                onDoneButtonClick={() =>
                  domainExposition({
                    domainExpositionRequest: {instagram: true},
                  })
                }
              >
                <div className={classes.shareSocialsCardContent}>
                  <p>1. Ingresa a tu página en Instagram.</p>
                  <p>
                    2. Clic en <b>Editar perfil.</b>
                  </p>
                  <p>
                    3. Copia tu dominio:{' '}
                    <b className={classes.shareSocialsDomain}>
                      {!!store && getCustomDomain(store)}
                    </b>
                    <ClickAwayListener onClickAway={() => setLinkCopied(0)}>
                      <Tooltip
                        placement="right"
                        arrow
                        // PopperProps={{
                        //   disablePortal: true,
                        // }}
                        onClose={() => setLinkCopied(0)}
                        open={linkCopied === 2}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        leaveDelay={300}
                        title="¡Link copiado!"
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            !!store &&
                              store.domains[0].domainName &&
                              navigator &&
                              navigator.clipboard &&
                              navigator.clipboard
                                .writeText(`https://${getCustomDomain(store)}`)
                                .then(() => setLinkCopied(2))
                                .catch(e => console.error(e));
                          }}
                        >
                          <FileCopyOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ClickAwayListener>
                  </p>
                  <p>4. Pegarlo como sitio web</p>
                  <img
                    className={classes.shareSocialsCardImage}
                    src={shareToInstagramExample}
                    alt="compartir en instagram"
                  />
                </div>
              </ShareSocialsCard>
            </div>
          </main>
        </Route>
      </Switch>
      <ComparisonTierModal
        open={comparisonModal}
        handleClose={() => setComparisonModal(false)}
      />
      <ReferralModal
        open={referralModal}
        handleCloseModal={() => setReferralModal(false)}
      />
      <UploadProductModal
        open={uploadProductModal}
        handleCloseModal={() => setUploadProductModal(false)}
      />
      <TestOrdersModal
        open={orderModal}
        handleClose={() => {
          setOrderModal(false);
          history.replace({search: ''});
        }}
      />
    </div>
  );
}

Home.propTypes = {
  user: PropTypes.object,
  store: PropTypes.object,
  resellerInformation: PropTypes.object,
  isFetchingResellerInformation: PropTypes.bool,
  fetchProducts: PropTypes.func.isRequired,
  productsCount: PropTypes.number,
  fetchOrdersCount: PropTypes.func.isRequired,
  ordersCount: PropTypes.number,
  categoriesCount: PropTypes.number,
  socialMedia: PropTypes.array,
  fetchPageViews: PropTypes.func.isRequired,
  pageViews: PropTypes.number,
  fetchSummaryProducts: PropTypes.func,
  productData: PropTypes.object,
  fetchSummaryOrders: PropTypes.func,
  orderData: PropTypes.array,
  fetchSummaryProductCategories: PropTypes.func,
  productCategoryData: PropTypes.object,
  completeFirstSteps: PropTypes.func.isRequired,
  fetchCompleteFirstSteps: PropTypes.func.isRequired,
  firstStepsCompleted: PropTypes.bool,
  isCheckingIfFirstStepsCompleted: PropTypes.bool,
  domainExposition: PropTypes.func.isRequired,
  fetchDomainExposition: PropTypes.func.isRequired,
  domainExpositionData: PropTypes.object,
  referreds: PropTypes.array,
  points: PropTypes.number,
  subscription: PropTypes.object,
  subscriptionSetting: PropTypes.object,
};
